import React from "react";
import RichiesteService from "../../../services/RichiesteService";
import EmptyStateCard from "../../../components/shared/EmptyStateCard";
import PageHero from "../../../components/shared/PageHero";
import SummaryMetricCard from "../../../components/shared/SummaryMetricCard";
import CustomerRequestCard from "../../../components/marketplace/requests/CustomerRequestCard";
import { getDateSortValue } from "../../../utils/date";
import { isRichiestaExpired } from "../../../utils/richiestaExpiry";
import "../../../styles/screens/marketplace/requests/LeMieRichieste.css";

const getRequestDisplayTitle = (richiesta) => {
  const title = (richiesta?.titolo || "").trim();
  if (title && !/^Richiesta farmaci/i.test(title)) {
    return title;
  }

  const prodotti = Array.isArray(richiesta?.prodotti) ? richiesta.prodotti : [];
  if (prodotti.length === 0) {
    return title || "Ordine marketplace";
  }

  const firstName = prodotti[0]?.nome || "Prodotto";
  return prodotti.length === 1
    ? `Ordine ${firstName}`
    : `Ordine ${firstName} +${prodotti.length - 1}`;
};

const getBestOffer = (offerte = []) => {
  if (!Array.isArray(offerte) || offerte.length === 0) {
    return null;
  }

  return offerte.reduce((best, current) => {
    if (!best) {
      return current;
    }

    return Number(current.prezzoTotale || 0) < Number(best.prezzoTotale || 0)
      ? current
      : best;
  }, null);
};
const getAcceptedOffer = (richiesta) => {
  const offerte = Array.isArray(richiesta?.offerte) ? richiesta.offerte : [];
  const acceptedOfferId = richiesta?.offertaAccettataId || null;

  return (
    offerte.find((offerta) => {
      const offerId = offerta?.id || offerta?.sellerId;
      return ["SELEZIONATA", "ACCETTATA"].includes(offerta?.stato) || (acceptedOfferId && offerId === acceptedOfferId);
    }) || null
  );
};

class LeMieRichieste extends React.Component {
  state = {
    richieste: [],
    isLoading: true,
    error: "",
  };

  componentDidMount() {
    this.loadRichieste();
  }

  componentDidUpdate(prevProps) {
    if (Boolean(prevProps.showStorico) !== Boolean(this.props.showStorico)) {
      this.loadRichieste();
    }
  }

  loadRichieste = async () => {
    const isStoricoMode = Boolean(this.props.showStorico);
    this.setState({
      isLoading: true,
      error: "",
    });

    try {
      const response = await RichiesteService.list({
        view: isStoricoMode ? "history" : "active",
      });

      this.setState({
        richieste: (Array.isArray(response?.items) ? response.items : []).sort(
          (left, right) => getDateSortValue(right?.createdAt) - getDateSortValue(left?.createdAt)
        ),
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        richieste: [],
        isLoading: false,
        error: "Impossibile caricare le tue richieste. Riprova tra poco.",
      });
    }
  };

  renderRequestCard = (richiesta) => {
    const offerte = Array.isArray(richiesta.offerte) ? richiesta.offerte : [];
    const bestOffer = getBestOffer(offerte);
    const acceptedOffer = getAcceptedOffer(richiesta);
    const paymentState = richiesta?.pagamento?.stato || "NON_AVVIATO";
    const isPaid = paymentState === "PAGATO" || richiesta?.stato === "PAGATA";
    const isInProgress = richiesta?.stato === "IN_LAVORAZIONE";
    const isPaymentPending = paymentState === "IN_ATTESA";

    return (
      <CustomerRequestCard
        key={richiesta.id || richiesta._id}
        richiesta={richiesta}
        title={getRequestDisplayTitle(richiesta)}
        bestOffer={bestOffer}
        acceptedOffer={acceptedOffer}
        isPaid={isPaid}
        isInProgress={isInProgress}
        isPaymentPending={isPaymentPending}
      />
    );
  };

  render() {
    const { richieste, isLoading, error } = this.state;
    const isStoricoMode = Boolean(this.props.showStorico);
    const richiesteAttive = richieste.filter((item) => !isRichiestaExpired(item));
    const richiesteStorico = richieste.filter((item) => isRichiestaExpired(item));
    const richiesteVisibili = isStoricoMode ? richiesteStorico : richiesteAttive;
    const richiesteConOfferte = richieste.filter((item) => (item.offerte || []).length > 0).length;
    const richiesteScadute = richiesteStorico.length;

    return (
      <div className="customer-requests-shell">
        <div className="container-fluid">
          <PageHero
            className="customer-requests-hero"
            eyebrow="Area cliente"
            title={isStoricoMode ? "Storico richieste" : "Le mie richieste"}
            description={
              isStoricoMode
                ? "Elenco delle richieste scadute senza offerta accettata."
                : "Monitora le richieste attive inviate al marketplace e tieni subito d'occhio le offerte gia ricevute."
            }
          />

          <section className="customer-requests-summary marketplace-summary-grid marketplace-summary-grid--three">
            <SummaryMetricCard label="Richieste totali" value={richieste.length} className="customer-requests-summary__card" />
            <SummaryMetricCard label="Con offerte ricevute" value={richiesteConOfferte} className="customer-requests-summary__card" />
            <SummaryMetricCard label="Scadute (24h)" value={richiesteScadute} className="customer-requests-summary__card" />
          </section>

          <section className="customer-requests-list">
            {error ? <div className="alert alert-danger">{error}</div> : null}

            {isLoading ? (
              <EmptyStateCard
                className="customer-requests-empty-state"
                icon="fa-circle-o-notch"
                spin={true}
                title="Caricamento richieste"
                description="Stiamo recuperando le richieste inviate dal tuo account."
              />
            ) : null}

            {!isLoading && !error && richiesteVisibili.length === 0 ? (
              <EmptyStateCard
                className="customer-requests-empty-state"
                icon={isStoricoMode ? "fa-archive" : "fa-shopping-basket"}
                title={isStoricoMode ? "Storico vuoto" : "Nessuna richiesta attiva"}
                description={
                  isStoricoMode
                    ? "Le richieste scadute compariranno qui."
                    : "Quando invierai una nuova richiesta, la ritroverai qui finche resta attiva."
                }
              />
            ) : null}

            {!isLoading && !error ? richiesteVisibili.map(this.renderRequestCard) : null}
          </section>
        </div>
      </div>
    );
  }
}

export default LeMieRichieste;
















